import { useAddress } from "@redotech/redo-customer-portal-shared/hooks/useAddress/use-address";
import {
  AdjustmentModeType,
  AdjustmentType,
  useItemAdjustments,
} from "@redotech/redo-customer-portal-shared/hooks/useItemAdjustments/use-item-adjustments";
import { MerchantOverrides } from "@redotech/redo-customer-portal-shared/hooks/useReview/use-merchant-overrides";
import { Navigator } from "@redotech/redo-customer-portal-shared/navigator";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { CompensationMethodType } from "@redotech/redo-model/draft-return/draft-return";
import { Channel } from "@redotech/redo-model/return";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import AlertCircleIcon from "@redotech/redo-web/arbiter-icon/alert-circle.svg";
import { Flex } from "@redotech/redo-web/flex";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import {
  SelectDropdown,
  SelectDropdownTheme,
} from "@redotech/redo-web/select-dropdown";
import { Text } from "@redotech/redo-web/text";
import { SpacingValue } from "@redotech/redo-web/theme/box";
import { useState } from "react";
import { Addresses } from "../../components/addresses";
import * as reviewCss from "./review.module.css";

export const MerchantOverrideDetailsSection = ({
  overrides,
  pxOverride,
  selectedCompensationMethod,
}: {
  overrides: MerchantOverrides;
  pxOverride: SpacingValue;
  selectedCompensationMethod?: CompensationMethodType | null;
}) => {
  if (!overrides) {
    return null;
  }
  const { texts, inputs } = overrides;
  const isRepairMethod =
    selectedCompensationMethod === CompensationMethodType.REPAIR;

  return (
    <Flex
      className={reviewCss.styledCard}
      flexDirection="column"
      gap="2xl"
      grow={1}
      px={pxOverride}
      py="3xl"
    >
      <Text fontSize="lg" fontWeight="semibold" textColor="primary">
        {texts.detailsHeader}
      </Text>
      <RedoToggle
        description={texts.flagReturnDescription}
        disabled={!inputs.enabled}
        label={texts.flagReturn}
        setValue={inputs.onChangeFlagReturn}
        size="sm"
        value={inputs.flagReturn}
      />
      {isRepairMethod && (
        <RedoToggle
          description={texts.createRepairInvoiceDescription}
          disabled={!inputs.enabled}
          label={texts.createRepairInvoice}
          setValue={inputs.onChangeCreateRepairInvoice}
          size="sm"
          value={inputs.createRepairInvoice}
        />
      )}
      <RedoToggle
        description={texts.manualReviewDescription}
        disabled={!inputs.enabled}
        label={texts.manualReview}
        setValue={inputs.onChangeManualReview}
        size="sm"
        value={inputs.manualReview}
      />
    </Flex>
  );
};

export const MerchantOverrideShippingLabelPayers = ({
  overrides,
}: {
  overrides: MerchantOverrides;
}) => {
  if (!overrides) {
    return null;
  }
  const {
    texts,
    inputs,
    showLabelPayerToggle,
    showNewOrderLabelPayerToggle,
    showMerchantPaysReturnShippingDescription,
  } = overrides;
  return (
    <Flex flexDirection="column" gap="2xl">
      {showLabelPayerToggle && (
        <RedoToggle
          description={texts.customerPaysReturnShippingDescription}
          disabled={!inputs.enabled}
          label={texts.customerPaysReturnShipping}
          setValue={inputs.onChangeCustomerPaysReturnShipping}
          size="sm"
          value={inputs.customerPaysReturnShipping}
        />
      )}
      {showMerchantPaysReturnShippingDescription && (
        <Text fontSize="xs" textColor="tertiary">
          {texts.merchantPaysReturnShippingDescription}
        </Text>
      )}
      {showNewOrderLabelPayerToggle && (
        <RedoToggle
          description={texts.customerPaysNewOrderShippingDescription}
          disabled={!inputs.enabled}
          label={texts.customerPaysNewOrderShipping}
          setValue={inputs.onChangeCustomerPaysNewOrderShipping}
          size="sm"
          value={inputs.customerPaysNewOrderShipping}
        />
      )}
      <Text fontSize="xs" textColor="tertiary">
        {texts.labelDescription}
      </Text>
    </Flex>
  );
};

export const MerchantOverrideDiscountsSection = ({
  overrides,
  pxOverride,
}: {
  overrides: MerchantOverrides;
  pxOverride: SpacingValue;
}) => {
  if (!overrides || !overrides.showDiscountSection) {
    return null;
  }
  const { texts, inputs } = overrides;
  return (
    <Flex
      className={reviewCss.styledCard}
      flexDirection="column"
      gap="2xl"
      grow={1}
      px={pxOverride}
      py="3xl"
    >
      <Text fontSize="lg" fontWeight="semibold" textColor="primary">
        {texts.discountsHeader}
      </Text>
      <RedoTagInputField
        description={texts.discountInputDescription}
        inputValue={inputs.newDiscountCode}
        label={texts.discountInputLabel}
        setInputValue={inputs.onChangeNewDiscountCode}
        setTags={inputs.onChangeDiscountCodes}
        state={inputs.enabled ? "default" : "disabled"}
        tags={inputs.discountCodes.map((code) => ({ text: code }))}
      />
      <RedoCheckbox
        disabled={!inputs.enabled}
        label={texts.applyAutomaticDiscountsLabel}
        setValue={inputs.onChangeApplyAutomaticDiscounts}
        size="sm"
        value={inputs.applyAutomaticDiscounts}
      />
    </Flex>
  );
};

export const MerchantOverrideAdjustmentModal = ({
  overrides,
  isOpen,
  onClose,
  pendingReturnItemId,
  itemReturnValue,
}: {
  overrides: NonNullable<MerchantOverrides>;
  isOpen: boolean;
  onClose: () => void;
  pendingReturnItemId: string;
  itemReturnValue: number;
}) => {
  const { texts } = overrides;
  const {
    adjustmentType,
    adjustmentMode,
    adjustmentValue,
    onAdjustmentValueChange,
    isInputValid,
    onApply,
    formatInputValue,
    onAdjustmentTypeChange,
    onAdjustmentModeChange,
  } = useItemAdjustments({
    overrides,
    pendingReturnItemId,
    itemReturnValue,
    onClose,
  });

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: texts.adjustmentModalApplyButton,
        onClick: () => onApply(adjustmentType, adjustmentMode),
        size: "sm",
      }}
      secondaryButton={{
        text: texts.adjustmentModalCancelButton,
        onClick: onClose,
        size: "sm",
      }}
      title={texts.adjustmentModalTitle}
    >
      <Flex flexDirection="column" gap="3xl">
        <RedoButtonTabGroup<AdjustmentType>
          onChange={(value: AdjustmentType) => onAdjustmentTypeChange(value)}
          options={[
            { value: AdjustmentType.FEE, el: <>{AdjustmentType.FEE}</> },
            { value: AdjustmentType.BONUS, el: <>{AdjustmentType.BONUS}</> },
          ]}
          size="sm"
          value={adjustmentType}
        />
        <LabeledInput label={adjustmentMode}>
          <RedoTextInput
            inputTypeHint="number"
            onBlur={() => formatInputValue(adjustmentValue)}
            setValue={onAdjustmentValueChange}
            size="md"
            state={isInputValid ? "default" : "error"}
            trailingActions={
              <SelectDropdown
                className={reviewCss.inlineCurrencyDropdown}
                display={(mode) => (
                  <Flex align="center" gap="xs">
                    <Text fontSize="sm" textColor="tertiary">
                      {mode === AdjustmentModeType.AMOUNT ? "$" : "%"}
                    </Text>
                  </Flex>
                )}
                options={[
                  AdjustmentModeType.AMOUNT,
                  AdjustmentModeType.PERCENTAGE,
                ]}
                showChevron
                theme={SelectDropdownTheme.GHOST}
                value={adjustmentMode}
                valueChange={(mode) => mode && onAdjustmentModeChange(mode)}
              >
                {(mode) => (
                  <Text fontSize="xs" textColor="primary">
                    {mode === AdjustmentModeType.AMOUNT ? "$" : "%"}
                  </Text>
                )}
              </SelectDropdown>
            }
            value={adjustmentValue}
          />
        </LabeledInput>
      </Flex>
    </RedoModal>
  );
};

export const MerchantOverrideItemAdjustments = ({
  overrides,
  pendingReturnItemId,
  itemReturnValue,
}: {
  overrides: MerchantOverrides;
  pendingReturnItemId: string | undefined;
  itemReturnValue?: number;
}) => {
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  if (!overrides || !pendingReturnItemId || !itemReturnValue) {
    return null;
  }
  const { texts, inputs, permissions } = overrides;
  return (
    <Flex flexDirection="column" gap="lg">
      {permissions.canEditFeesAndBonuses && (
        <Flex style={{ width: "155px" }}>
          <RedoButton
            disabled={!inputs.enabled}
            hierarchy="secondary"
            onClick={() => setIsAdjustmentModalOpen(true)}
            size="sm"
            text={texts.adjustFeeButton}
          />
        </Flex>
      )}
      {permissions.canEditGreenReturns && (
        <RedoToggle
          description={texts.greenReturnDescription}
          disabled={!inputs.enabled}
          label={texts.greenReturn}
          setValue={(value) =>
            inputs.onChangeGreenReturnOverride(pendingReturnItemId, value)
          }
          size="sm"
          testId="green-return-toggle"
          value={inputs.greenReturnOverrides.get(pendingReturnItemId) ?? false}
        />
      )}
      {isAdjustmentModalOpen && permissions.canEditFeesAndBonuses && (
        <MerchantOverrideAdjustmentModal
          isOpen={isAdjustmentModalOpen}
          itemReturnValue={itemReturnValue}
          onClose={() => setIsAdjustmentModalOpen(false)}
          overrides={overrides}
          pendingReturnItemId={pendingReturnItemId}
        />
      )}
    </Flex>
  );
};

export const MerchantOverrideAddressesSection = ({
  navigation,
  settings,
  channel,
  overrides,
}: {
  navigation: Navigator;
  settings: ReturnAppSettings | undefined;
  channel: Channel;
  overrides: MerchantOverrides;
}) => {
  const { addresses } = useAddress({
    navigator: navigation,
    settings,
    channel,
  });

  return (
    <>
      <Addresses addresses={addresses} showBorder={false} />
      <MerchantOverrideShippingLabelPayers overrides={overrides} />
    </>
  );
};

export const MerchantOverrideItemRejectMessage = ({
  merchantCreatedPreRejectMessage,
}: {
  merchantCreatedPreRejectMessage: string;
}) => {
  return (
    <Flex align="center" gap="xs">
      <RedoFeaturedIcon color="warning" Icon={AlertCircleIcon} size="xs" />
      <Text fontSize="xs" fontWeight="bold" textColor="disabled">
        {merchantCreatedPreRejectMessage}
      </Text>
    </Flex>
  );
};
